import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, LIBRARY_LIMIT } from "@/lib/api";
import type { DateRange, QuestionRating, Role, TagInput, UserSettings } from "@/types";

// ============================================================================
// Query keys — centralised so invalidation stays consistent.
// ============================================================================
export const queryKeys = {
  session: ["session"] as const,
  documents: (params?: unknown) => ["documents", params ?? {}] as const,
  document: (id: string) => ["documents", id] as const,
  questions: (params?: unknown) => ["questions", params ?? {}] as const,
  question: (id: string) => ["questions", id] as const,
  questionSources: (id: string) => ["questions", id, "sources"] as const,
  questionCandidates: (id: string) => ["questions", id, "candidates"] as const,
  questionUsage: (id: string) => ["questions", id, "usage"] as const,
  citedPages: (id: string) => ["questions", id, "cited-pages"] as const,
  questionRatings: (id: string) => ["questions", id, "ratings"] as const,
  questionComments: (id: string) => ["questions", id, "comments"] as const,
  documentPage: (id: string, page: number) => ["documents", id, "page", page] as const,
  filters: ["filters"] as const,
  librarySearches: ["library-searches"] as const,
  settings: ["settings"] as const,
  users: ["users"] as const,
  invitations: ["invitations"] as const,
  tags: ["tags"] as const,
  analytics: (report: string, range: DateRange) =>
    ["analytics", report, range.start, range.end] as const,
};

/**
 * Retrying an authentication or authorisation failure is pointless — the token
 * will not have improved — and retrying a 403 for a user with no org just
 * delays the message that tells them what to do.
 */
function retryPolicy(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useSessionQuery() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: api.session,
    retry: retryPolicy,
    staleTime: 5 * 60_000,
  });
}

export function useFiltersQuery() {
  return useQuery({
    queryKey: queryKeys.filters,
    queryFn: api.filters,
    retry: retryPolicy,
    staleTime: 60_000,
  });
}

/** Not still ingesting, just stuck: no faster polling for these. */
const INGESTING_WINDOW_MS = 24 * 60 * 60_000;

/** The whole library, as the Library page sorts, filters and exports it. */
export function useDocumentsQuery() {
  return useQuery({
    queryKey: queryKeys.documents({ limit: LIBRARY_LIMIT }),
    queryFn: () => api.documents({ limit: LIBRARY_LIMIT }),
    retry: retryPolicy,
    // Two cadences, for two different things happening off-screen.
    //
    // A document being ingested changes state without anything in this tab
    // doing so, and five seconds is what makes an upload appear to finish
    // rather than needing a manual refresh. A quiet library still gains
    // documents — the processor pushes them in — so it polls at ERIKA's
    // fifty seconds rather than not at all, which is the difference between a
    // librarian seeing a new arrival and being told about it.
    //
    // "Being ingested" means processing and added in the last day. Older ones
    // are stuck — ERIKA imports that never got vectors — and would otherwise
    // hold every open Library tab at five seconds for good.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 50_000;
      const now = Date.now();
      const ingesting = data.documents.some(
        (d) => d.status === "processing" && now - Date.parse(d.createdAt) < INGESTING_WINDOW_MS,
      );
      return ingesting ? 5_000 : 50_000;
    },
  });
}

export function useDocumentQuery(documentId: string) {
  return useQuery({
    queryKey: queryKeys.document(documentId),
    queryFn: () => api.document(documentId),
    retry: retryPolicy,
    // Page image URLs are signed and short-lived, so a detail page left open
    // for an hour must re-fetch rather than render broken images.
    staleTime: 5 * 60_000,
  });
}

export function useQuestionsQuery(params: { search?: string; mine?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.questions(params),
    queryFn: () => api.questions(params),
    retry: retryPolicy,
    // While any listed question is still being answered, the list checks
    // again every few seconds, so "Working" turns into "Answered" without a
    // reload. Otherwise it is left alone.
    refetchInterval: (query) =>
      query.state.data?.questions.some((q) => q.status === "pending" || q.status === "running")
        ? 5_000
        : false,
  });
}

export function useQuestionQuery(questionId: string) {
  return useQuery({
    queryKey: queryKeys.question(questionId),
    queryFn: () => api.question(questionId),
    retry: retryPolicy,
    // A question opened while it is still running — a shared link, a second
    // tab, a reload mid-answer — resolves itself without the viewer doing
    // anything.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "running" || status === "pending" ? 3_000 : false;
    },
  });
}

export function useQuestionSourcesQuery(questionId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.questionSources(questionId),
    queryFn: () => api.questionSources(questionId),
    retry: retryPolicy,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * The optimiser's rewrites of one question.
 *
 * Fetched only once the question is answered: before that the candidates may
 * not exist yet, and a poll on a running question is a request per second for
 * a row that is not there.
 */
export function useQuestionCandidatesQuery(questionId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.questionCandidates(questionId),
    queryFn: () => api.questionCandidates(questionId),
    retry: retryPolicy,
    enabled,
    // Candidates never change once written. Refetching them is pure cost.
    staleTime: Infinity,
  });
}

/** What one question cost at the gateway, by stage. */
export function useQuestionUsageQuery(questionId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.questionUsage(questionId),
    queryFn: () => api.questionUsage(questionId),
    retry: retryPolicy,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useRemoveDocumentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.removeDocument(documentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      // Removing a document changes the corpus the filters are derived from.
      void qc.invalidateQueries({ queryKey: queryKeys.filters });
    },
  });
}

export function useDeleteQuestionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) => api.deleteQuestion(questionId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["questions"] }),
  });
}

export function useResumeQuestionMutation(questionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.resumeQuestion(questionId),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.question(questionId), data);
    },
  });
}

/**
 * The Library page's plain-language search.
 *
 * A mutation rather than a query because it is an action with a cost — one
 * embedding call per search — and must fire when the user submits, not
 * whenever a key is pressed or the component remounts.
 */
export function useLibrarySearchMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ query, audiences, topics }: { query: string; audiences?: string[]; topics?: string[] }) =>
      api.searchLibrary(query, { audiences, topics }),
    // The search endpoint is what records the history, so the list is stale
    // the moment one comes back.
    onSettled: () => void qc.invalidateQueries({ queryKey: queryKeys.librarySearches }),
  });
}

export function useLibrarySearchHistoryQuery() {
  return useQuery({
    queryKey: queryKeys.librarySearches,
    queryFn: () => api.librarySearchHistory(),
    retry: retryPolicy,
  });
}

// ---------------------------------------------------------------------------
// Cited pages
// ---------------------------------------------------------------------------

/**
 * The page images behind an answer's citations.
 *
 * `staleTime` is shorter than the signed URLs' TTL on purpose: the images stop
 * loading when the signature expires, so the cache entry has to die first.
 */
export function useCitedPagesQuery(questionId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.citedPages(questionId),
    queryFn: () => api.citedPages(questionId),
    retry: retryPolicy,
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * One page of a document, for the viewer's forward and back arrows.
 *
 * Fetching a page at a time is what lets a reader walk the context around a
 * cited claim without the app downloading a whole PDF first. Each page is
 * cached, so paging back over ground already covered is instant.
 */
export function useDocumentPageQuery(documentId: string, page: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.documentPage(documentId, page),
    queryFn: () => api.documentPage(documentId, page),
    retry: retryPolicy,
    enabled: enabled && page > 0,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Administration
// ---------------------------------------------------------------------------

export function useUsersQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    retry: retryPolicy,
    enabled,
  });
}

export function useUpdateUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; role?: Role; revoked?: boolean }) =>
      api.updateUser(id, patch),
    // The server returns the whole list because one role change can move the
    // last-owner constraint for every other row; writing it straight into the
    // cache means the table's controls are correct without a second round trip.
    onSuccess: (data) => qc.setQueryData(queryKeys.users, data),
  });
}

export function useInvitationsQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.invitations,
    queryFn: api.invitations,
    retry: retryPolicy,
    enabled,
  });
}

// Both mutations return the whole pending list, written straight into the
// cache as the user mutations do.
export function useInviteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: Role }) => api.invite(email, role),
    onSuccess: (data) => qc.setQueryData(queryKeys.invitations, data),
  });
}

export function useCancelInvitationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.cancelInvitation(id),
    onSuccess: (data) => qc.setQueryData(queryKeys.invitations, data),
  });
}

/**
 * This person's Ask-form defaults.
 *
 * A long stale time because the only thing that changes them is the drawer on
 * this same page, and that writes the answer straight into the cache.
 */
export function useSettingsQuery() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: api.settings,
    retry: retryPolicy,
    staleTime: 10 * 60_000,
  });
}

export function useSaveSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: UserSettings) => api.saveSettings(s),
    // The server echoes what it stored, so writing that into the cache is
    // both the optimistic update and the confirmation.
    onSuccess: (data) => qc.setQueryData(queryKeys.settings, data),
  });
}

export function useTagsQuery() {
  return useQuery({
    queryKey: queryKeys.tags,
    queryFn: api.tags,
    retry: retryPolicy,
  });
}

/**
 * Add a tag or edit an existing one.
 *
 * The tag list is invalidated rather than patched: the response carries the
 * saved row but not its document count, and writing a confident zero into the
 * table would be worse than a refetch.
 */
export function useSaveTagMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TagInput) => api.saveTag(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.tags });
      void qc.invalidateQueries({ queryKey: queryKeys.filters });
    },
  });
}

export function useSetTagActiveMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.setTagActive(id, active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.tags });
      void qc.invalidateQueries({ queryKey: queryKeys.filters });
    },
  });
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

/**
 * The four analytics reports for one window.
 *
 * They are four queries rather than one endpoint returning everything, because
 * the overview is cheap and the citation extraction behind the document report
 * is not — and the page renders its headline numbers while the tables are
 * still loading rather than showing an empty screen until the slowest one
 * lands.
 */
export function useAnalyticsQueries(range: DateRange) {
  const overview = useQuery({
    queryKey: queryKeys.analytics("overview", range),
    queryFn: () => api.analyticsOverview(range),
    retry: retryPolicy,
    staleTime: 60_000,
  });
  const activity = useQuery({
    queryKey: queryKeys.analytics("activity", range),
    queryFn: () => api.analyticsActivity(range),
    retry: retryPolicy,
    staleTime: 60_000,
  });
  const documents = useQuery({
    queryKey: queryKeys.analytics("documents", range),
    queryFn: () => api.analyticsDocuments(range),
    retry: retryPolicy,
    staleTime: 60_000,
  });
  const users = useQuery({
    queryKey: queryKeys.analytics("users", range),
    queryFn: () => api.analyticsUsers(range),
    retry: retryPolicy,
    staleTime: 60_000,
  });
  return { overview, activity, documents, users };
}

// ---------------------------------------------------------------------------
// Ratings
// ---------------------------------------------------------------------------

export function useQuestionRatingsQuery(questionId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.questionRatings(questionId),
    queryFn: () => api.questionRatings(questionId),
    retry: retryPolicy,
    enabled,
  });
}

export function useSaveRatingMutation(questionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rating: QuestionRating) => api.saveQuestionRating(questionId, rating),
    onSuccess: (data) => {
      // The server returned the recomputed aggregate, so this is written
      // straight in rather than invalidated — the summary must never render a
      // frame where the viewer's new score is missing from the average.
      qc.setQueryData(queryKeys.questionRatings(questionId), data);
      // The question row and the previous-questions list both carry the
      // overall score, and both are now stale.
      void qc.invalidateQueries({ queryKey: queryKeys.question(questionId) });
      void qc.invalidateQueries({ queryKey: ["questions"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Discussion
// ---------------------------------------------------------------------------

/**
 * A question's comment thread.
 *
 * Polled, because a discussion is the one part of this page where someone else
 * is expected to change something while you are looking at it. ERIKA polled at
 * five seconds with the pane open and fifteen with it closed; this polls only
 * while the tab is focused, which is the same idea with less waste.
 */
export function useQuestionCommentsQuery(questionId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.questionComments(questionId),
    queryFn: () => api.questionComments(questionId),
    retry: retryPolicy,
    enabled,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

export function useAddCommentMutation(questionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.addQuestionComment(questionId, body),
    onSuccess: (data) => qc.setQueryData(queryKeys.questionComments(questionId), data),
  });
}

export function useDeleteCommentMutation(questionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => api.deleteQuestionComment(questionId, commentId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.questionComments(questionId) }),
  });
}

/**
 * Correct a document's metadata.
 *
 * The response is the refreshed detail, written straight into the cache — a
 * librarian fixing a year wants to see it fixed, not to watch a spinner while
 * the page refetches what the server just returned.
 */
export function useUpdateDocumentMutation(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Typed from the client rather than restated, so a field added to the
    // PATCH body is reachable from here without a second edit.
    mutationFn: (patch: Parameters<typeof api.updateDocument>[1]) =>
      api.updateDocument(documentId, patch),
    onSuccess: (detail) => {
      qc.setQueryData(queryKeys.document(documentId), detail);
      // The list shows title, year, audience and topics; the Ask page's
      // filters are derived from the same columns.
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: queryKeys.filters });
    },
  });
}
